import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemMetadata} from '@github-ui/list-view/ListItemMetadata'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {TableIcon} from '@primer/octicons-react'
import {Label, RelativeTime} from '@primer/react'
import {clsx} from 'clsx'
import {type ComponentProps, memo, type ReactNode} from 'react'

import styles from './RepositoryProjects.module.css'

export interface ProjectListItem {
  id: number | string
  number: number
  title: string
  shortDescription: string | null
  url: string
  updatedAt: string | null
  public: boolean
}

interface ProjectListRowProps {
  project: ProjectListItem
  secondaryActions?: ComponentProps<typeof ListItem>['secondaryActions']
  status?: ReactNode
  variant?: 'default' | 'planning'
}

function ProjectListRowComponent({project, secondaryActions, status, variant = 'default'}: ProjectListRowProps) {
  const isPlanning = variant === 'planning'
  const title = (
    <ListItemTitle
      value={
        isPlanning ? (
          <>
            <TableIcon className={styles.projectIcon} aria-hidden />
            {project.title}
          </>
        ) : (
          project.title
        )
      }
      href={project.url}
      headingClassName={isPlanning ? styles.projectTitle : undefined}
    >
      {!project.public && (
        <Label size="small" variant="secondary" className={styles.privateLabel}>
          Private
        </Label>
      )}
    </ListItemTitle>
  )

  return (
    <ListItem
      title={title}
      className={clsx(styles.projectRow, isPlanning && styles.planningRow)}
      alignment={isPlanning ? 'center' : undefined}
      secondaryActions={secondaryActions}
      metadata={status ? <ListItemMetadata alignment="right">{status}</ListItemMetadata> : undefined}
      metadataContainerClassName={isPlanning ? styles.projectStatus : undefined}
    >
      <ListItemMainContent>
        <ListItemDescription className={styles.listItemContent}>
          {project.shortDescription && <p className={styles.projectDescription}>{project.shortDescription}</p>}
          <div className={styles.projectMeta}>
            <span>
              #{project.number}
              {project.updatedAt && (
                <>
                  {' '}
                  updated <RelativeTime date={new Date(project.updatedAt)} />
                </>
              )}
            </span>
          </div>
        </ListItemDescription>
      </ListItemMainContent>
    </ListItem>
  )
}

export const ProjectListRow = memo(ProjectListRowComponent)
